import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { DataService, Note, NoteEditSessionSnapshot } from './data.service';
import { EditSessionService } from './edit-session.service';
import { extractSnapshotContent, snapshotContentEquals } from '../_util/note-snapshot.util';

/** One entry per note, however many leftover sessions it has. */
export interface AbandonedSessionReviewItem {
  noteId: string;
  /** The session whose opening version is offered: the earliest one — the note as it was before
   * any of these sessions. */
  sessionId: string;
  /** Every leftover session of this note (incl. `sessionId`); Keep / Restore clears them all. */
  sessionIds: string[];
  noteTitle: string;
  openedAt: string;
  lastActiveAt: string;
  currentStateLabel: string;
}

/**
 * Autosave Stage 3 — at app startup, looks for opening snapshots
 * (autosave Stage 2) left behind by edit sessions that never closed
 * normally (crashed tab, closed window, etc), and offers the user a
 * chance to recover or discard them. Never runs automatically beyond
 * this one startup check — restoring a snapshot is always an explicit
 * per-item user choice.
 */
@Injectable({ providedIn: 'root' })
export class AbandonedSessionRecoveryService {
  // Conservative "configurable" retention/abandonment threshold — a
  // snapshot's session must have been inactive at least this long before
  // it's ever considered abandoned. An ordinary page reload refreshes
  // lastActiveAt well inside this window (it's set at snapshot creation and
  // refreshed on every autosave, at most ~25s apart during active editing),
  // so a reload alone can never trigger recovery — only a session that
  // genuinely stopped autosaving (closed tab, crash, etc) can.
  private static readonly ABANDONMENT_THRESHOLD_MS = 5 * 60 * 1000;

  private reviewItemsSubject = new BehaviorSubject<AbandonedSessionReviewItem[]>([]);
  readonly reviewItems$ = this.reviewItemsSubject.asObservable();

  constructor(
    private dataService: DataService,
    private editSessionService: EditSessionService,
  ) {}

  async checkForAbandonedSessions(): Promise<void> {
    try {
      const snapshots = await this.dataService.getAllEditSessionSnapshots();
      if (!snapshots.length) {
        this.editSessionService.forgetEndedSessions(this.editSessionService.getEndedSessionIds());
        return;
      }

      // Sessions that ended normally while their window was closing (their snapshot delete may not
      // have completed): remove them without asking.
      const endedSessionIds = this.editSessionService.getEndedSessionIds();
      const ended = snapshots.filter((snapshot) => endedSessionIds.has(snapshot.sessionId));
      for (const snapshot of ended) {
        await this.dataService.deleteEditSessionSnapshot(snapshot.noteId, snapshot.sessionId);
      }
      // Ids with no snapshot left (deleted after all, or cleaned up just now) are no longer needed.
      const remainingIds = new Set(snapshots.filter((s) => !endedSessionIds.has(s.sessionId)).map((s) => s.sessionId));
      this.editSessionService.forgetEndedSessions([...endedSessionIds].filter((id) => !remainingIds.has(id)));

      // Windows/tabs of this app that are still open: their sessions aren't abandoned, even if they're
      // idle (a window left on another page simply hasn't ended its session yet). Only asked once.
      let liveTabIds: Set<string> | null = null;

      const leftoversByNote = new Map<string, NoteEditSessionSnapshot[]>();
      for (const snapshot of snapshots) {
        if (endedSessionIds.has(snapshot.sessionId)) continue;
        const ageMs = Date.now() - Date.parse(snapshot.lastActiveAt);
        if (!Number.isFinite(ageMs) || ageMs < AbandonedSessionRecoveryService.ABANDONMENT_THRESHOLD_MS) {
          continue; // recently active (including "just reloaded") — not abandoned
        }

        liveTabIds ??= await this.editSessionService.getLiveTabIds();
        if (snapshot.instanceId && liveTabIds.has(snapshot.instanceId)) continue;

        // Being edited live in another tab/window right now? Leave it to that
        // tab's own normal close/autosave lifecycle rather than interfering
        // (also covers "record-lock expiry": a stale live session doesn't count).
        const conflicting = await this.editSessionService.checkForConflictingSession(snapshot.noteId);
        if (conflicting) continue;

        const list = leftoversByNote.get(snapshot.noteId) ?? [];
        list.push(snapshot);
        leftoversByNote.set(snapshot.noteId, list);
      }

      const candidates: AbandonedSessionReviewItem[] = [];
      for (const [noteId, leftovers] of leftoversByNote) {
        const noteResult = await firstValueFrom(this.dataService.getNote(noteId));
        const note = noteResult.status === 'success' ? noteResult.data : undefined;
        if (!note) {
          // Note no longer exists locally — nothing to recover into.
          await this.deleteSnapshots(noteId, leftovers.map((s) => s.sessionId));
          continue;
        }

        // The current note already holds every autosaved edit made after the
        // snapshots were taken, so it — never an older snapshot — is treated
        // as the primary/default copy; a snapshot is only ever restored on
        // explicit request below. Sessions that changed nothing are cleaned up silently.
        const currentContent = extractSnapshotContent(note);
        const unchanged = leftovers.filter((s) => snapshotContentEquals(currentContent, s.content));
        await this.deleteSnapshots(noteId, unchanged.map((s) => s.sessionId));
        const changed = leftovers.filter((s) => !unchanged.includes(s));
        if (!changed.length) continue;

        // One entry per note: offer the earliest opening version (before all of these sessions).
        const byTime = (x: string) => Date.parse(x) || 0;
        const earliest = changed.reduce((a, b) => (byTime(b.openedAt) < byTime(a.openedAt) ? b : a));
        const lastActiveAt = changed.reduce((a, b) => (byTime(b.lastActiveAt) > byTime(a.lastActiveAt) ? b : a)).lastActiveAt;

        candidates.push({
          noteId,
          sessionId: earliest.sessionId,
          sessionIds: changed.map((s) => s.sessionId),
          noteTitle: note.Title || '(untitled note)',
          openedAt: earliest.openedAt,
          lastActiveAt,
          currentStateLabel: this.describeCurrentState(note),
        });
      }

      if (candidates.length > 0) {
        this.reviewItemsSubject.next(candidates);
      }
    } catch (err) {
      console.error('[AbandonedSessionRecovery] Startup check failed', err);
    }
  }

  private async deleteSnapshots(noteId: string, sessionIds: string[]): Promise<void> {
    for (const sessionId of sessionIds) {
      await this.dataService.deleteEditSessionSnapshot(noteId, sessionId);
    }
  }

  private describeCurrentState(note: Note): string {
    if (note.syncStatus === 'synced') return 'Saved and synchronized';
    if (note.syncStatus === 'new') return 'Saved locally, not yet created on the server';
    return 'Saved locally, awaiting sync';
  }

  async keepCurrentAndRemove(item: AbandonedSessionReviewItem): Promise<void> {
    await this.deleteSnapshots(item.noteId, this.allSessionIds(item));
    this.removeFromList(item);
  }

  /** Restores the opening snapshot's content into the live note as a new,
   * locally-dirty update — it does not touch _rev or sync/version history,
   * matching the same "new restoring update" behavior as Stage 2's discard. */
  async restoreOpeningVersion(item: AbandonedSessionReviewItem): Promise<void> {
    const [noteResult, snapshot] = await Promise.all([
      firstValueFrom(this.dataService.getNote(item.noteId)),
      this.dataService.getEditSessionSnapshot(item.noteId, item.sessionId),
    ]);
    const note = noteResult.status === 'success' ? noteResult.data : undefined;

    if (note && snapshot) {
      const restored: Note = { ...note, ...snapshot.content };
      await this.dataService.autosaveNote(restored);
    }

    await this.deleteSnapshots(item.noteId, this.allSessionIds(item));
    this.removeFromList(item);
  }

  private allSessionIds(item: AbandonedSessionReviewItem): string[] {
    return Array.from(new Set([item.sessionId, ...(item.sessionIds ?? [])]));
  }

  /** Leaves the snapshots untouched in IndexedDB; just dismisses this row
   * from the current dialog. Reconsidered again next startup. */
  reviewLater(item: AbandonedSessionReviewItem): void {
    this.removeFromList(item);
  }

  private removeFromList(item: AbandonedSessionReviewItem): void {
    this.reviewItemsSubject.next(
      this.reviewItemsSubject.value.filter((i) => !(i.noteId === item.noteId && i.sessionId === item.sessionId)),
    );
  }
}
